import adminApiClient from '../../api/base/adminApiClient'

/** 제안 처리 상태 */
export type PlaceSuggestionStatus = 'pending' | 'registered' | 'rejected'

export interface PlaceSuggestion {
  id: number
  store_name: string
  region: string
  status: PlaceSuggestionStatus
  /** 반려 사유 등 관리자 메모 */
  admin_note: string | null
  processed_at: string | null
  /**
   * 등록 처리한 경우 연결된 장소들.
   * 같은 상호가 여러 곳에 있어 특정할 수 없으면 후보를 여러 개 등록할 수 있다.
   */
  places: {
    id: number
    name: string
    address: string | null
  }[]
  user: {
    id: number
    username: string
    nickname: string
  } | null
  created_at: string
  updated_at: string
}

export interface PlaceSuggestionStatusCounts {
  pending: number
  registered: number
  rejected: number
}

export interface GetPlaceSuggestionsResponse {
  data: PlaceSuggestion[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  status_counts: PlaceSuggestionStatusCounts
}

export interface GetPlaceSuggestionsParams {
  page?: number
  per_page?: number
  search?: string
  status?: PlaceSuggestionStatus
}

/** 카카오맵 링크 해석 결과 (아직 저장되지 않은 미리보기) */
export interface ResolveKakaoLinkResponse {
  kakao_place_id: string
  name: string | null
  address: string | null
  lat: number | null
  lng: number | null
  /** 일부만 읽었을 때의 안내 문구 */
  warning: string | null
  /** 같은 카카오 장소가 이미 등록돼 있으면 그 장소 */
  existing_place: {
    id: number
    name: string
    address: string | null
      lat: number | string | null
    lng: number | string | null
  } | null
}

export interface RegisterPlaceRequest {
  name: string
  address: string
  lat: number
  lng: number
  place_category_id: number
  kakao_place_id?: string | null
  /** 앱에 보일 세부 분류 라벨. 비우면 라벨 없이 등록된다 */
  sub_category?: string | null
}

export interface AdminMapSettings {
  cluster_dissolve_zoom: number
}

export interface GetAdminMapSettingsResponse {
  data: AdminMapSettings
}

export interface UpdateAdminMapSettingsRequest {
  cluster_dissolve_zoom: number
}

export const adminMapApi = {
  getPlaceSuggestions: async (
    params?: GetPlaceSuggestionsParams
  ): Promise<GetPlaceSuggestionsResponse> => {
    const response = await adminApiClient.get<GetPlaceSuggestionsResponse>(
      '/admin/place-suggestions',
      {
        params,
      }
    )
    return response.data
  },
  /** 카카오맵 링크에서 상호명·주소·좌표를 읽어온다 (저장하지 않음) */
  resolveKakaoPlaceLink: async (
    url: string
  ): Promise<ResolveKakaoLinkResponse> => {
    const response = await adminApiClient.post<ResolveKakaoLinkResponse>(
      '/admin/place-suggestions/resolve-link',
      { url }
    )
    return response.data
  },
  /**
   * 주소를 좌표로 바꾼다.
   *
   * 링크 해석이 막혀도(카카오가 2026-09-22부터 장소 페이지를 403으로 차단)
   * 주소만 있으면 좌표를 얻을 수 있어야 등록이 끝난다. 이쪽은 공식 주소 검색
   * API라 그 차단과 무관하다.
   */
  geocodeAddress: async (
    address: string
  ): Promise<{ lat: number; lng: number; address: string | null }> => {
    const response = await adminApiClient.post<{
      lat: number
      lng: number
      /** 층·호수·건물명을 뗀 도로명 주소. 표기도 DB 표준에 맞춰져 있다. */
      address: string | null
    }>('/admin/place-suggestions/geocode', { address })
    return response.data
  },
  /** 제안을 실제 장소로 등록한다 */
  registerPlaceFromSuggestion: async (
    id: number,
    data: RegisterPlaceRequest
  ): Promise<{ message: string; data: PlaceSuggestion }> => {
    const response = await adminApiClient.post<{
      message: string
      data: PlaceSuggestion
    }>(`/admin/place-suggestions/${id}/register`, data)
    return response.data
  },
  /** 제안을 반려하거나 미처리로 되돌린다 */
  updatePlaceSuggestionStatus: async (
    id: number,
    status: 'pending' | 'rejected',
    adminNote?: string
  ): Promise<{ message: string; data: PlaceSuggestion }> => {
    const response = await adminApiClient.patch<{
      message: string
      data: PlaceSuggestion
    }>(`/admin/place-suggestions/${id}/status`, {
      status,
      admin_note: adminNote || null,
    })
    return response.data
  },
  /** 제안에 잘못 연결한 장소를 떼어낸다 (장소 자체는 남는다) */
  detachPlaceFromSuggestion: async (
    id: number,
    placeId: number
  ): Promise<{ message: string; data: PlaceSuggestion }> => {
    const response = await adminApiClient.delete<{
      message: string
      data: PlaceSuggestion
    }>(`/admin/place-suggestions/${id}/places/${placeId}`)
    return response.data
  },
  /** 제안 없이 장소를 직접 등록한다 */
  createPlaceFromLink: async (
    data: RegisterPlaceRequest
  ): Promise<{ message: string; data: { id: number; name: string } }> => {
    const response = await adminApiClient.post<{
      message: string
      data: { id: number; name: string }
    }>('/admin/places', data)
    return response.data
  },
  getMapSettings: async (): Promise<GetAdminMapSettingsResponse> => {
    const response = await adminApiClient.get<GetAdminMapSettingsResponse>(
      '/admin/map-settings'
    )
    return response.data
  },
  updateMapSettings: async (
    data: UpdateAdminMapSettingsRequest
  ): Promise<GetAdminMapSettingsResponse> => {
    const response = await adminApiClient.put<GetAdminMapSettingsResponse>(
      '/admin/map-settings',
      data
    )
    return response.data
  },
}
